import { type ReactNode, useEffect, useRef, useState } from 'react';
import useResizeObserver from '@react-hook/resize-observer';
import { useDrag } from '@use-gesture/react';
import throttle from 'lodash/throttle';

import { useIsHovered } from '@tmh-website/services/hover.service';
import { Color } from '@tmh-website/shared/colors';
import { isiOS } from '@tmh-website/services/utils.service';

import * as Styled from './TextFadeOverflowScroll.styles';

export interface TextFadeOverflowScrollProps {
  fadeColor: Color;
  /**
   * Hide the fade if the background doesn't allow it (e.g. it is an image)
   */
  hideFade?: boolean;
  /**
   * How many pixels from the edge of the element, the scrolling should start
   */
  scrollBoundary?: number;
  /**
   * The factor of which the pixels of the border should be multiplied before the transform.
   * E.g. a multiplier of 0.5 moves the text 25px per interval (0.1s) if the cursor if 50px of the edge
   */
  speedMultiplier?: number;
  initialPosition?: 'start' | 'end';
  children: ReactNode;
}

interface Dimensions {
  container: {
    height: number;
    width: number;
    offsetLeft: number;
  };
  text: {
    width: number;
  };
  lengthDifference: number;
}

const TextFadeOverflowScroll = ({
  fadeColor,
  hideFade,
  scrollBoundary = 50,
  speedMultiplier = 0.5,
  initialPosition,
  children,
  ...props
}: TextFadeOverflowScrollProps) => {
  const textRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const cursorXPosition = useRef<number | undefined>();
  const [dimensions, setDimensions] = useState<Dimensions | null>(null);
  const [textPosition, setTextPosition] = useState<number>(0);
  const [isDragged, setIsDragged] = useState(false);
  const lastTextPosition = useRef<number | undefined>();
  const isHovered = useIsHovered(containerRef);

  const bind = useDrag(
    ({ first, last, movement }) => {
      if (first) {
        setIsDragged(true);
        lastTextPosition.current = textPosition;
      }
      setTextPosition(lastTextPosition.current! + movement[0]);
      if (last) {
        setIsDragged(false);
      }
    },
    {
      axis: 'x',
      bounds: { left: -(dimensions?.lengthDifference ?? 0), right: 0 },
      rubberband: true,
      mouseOnly: 'hover',
      pointer: { touch: true, keys: false },
    }
  );

  /**
   * Adjust values on resize
   */
  function updateDimensions() {
    const containerEl = containerRef.current;
    const textEl = textRef.current;

    if (containerEl && textEl) {
      const newDimensions = {
        container: {
          offsetLeft: containerEl.getBoundingClientRect().left,
          width: containerEl.clientWidth,
          height: containerEl.scrollHeight,
        },
        text: { width: textEl.scrollWidth },
        lengthDifference: Math.max(textEl.scrollWidth - containerEl.clientWidth, 0),
      };

      setDimensions(newDimensions);
    }
    moveText();
  }

  useResizeObserver(containerRef, updateDimensions);

  useEffect(() => {
    const timeout = setTimeout(updateDimensions, 100);
    return () => clearTimeout(timeout);
  }, [textRef.current, containerRef.current]);

  useEffect(() => {
    const throttledListener = throttle(updateDimensions, 250);
    window.addEventListener('resize', throttledListener);
    return () => {
      window.removeEventListener('resize', throttledListener);
    };
  }, []);

  useEffect(() => {
    window.requestAnimationFrame(updateDimensions);
    if (initialPosition === 'end' && !isiOS()) {
      window.requestAnimationFrame(() => setTextPosition(() => -(dimensions?.lengthDifference ?? 0)));
    }
  }, [children]);

  /**
   * Move text corresponding to the cursor position
   */
  function moveText() {
    if (cursorXPosition.current === undefined || !dimensions) {
      return;
    }
    // How the text should be move base on cursor position
    let moveTextInPx = 0;
    if (cursorXPosition.current <= scrollBoundary) {
      moveTextInPx = -(cursorXPosition.current - scrollBoundary);
    } else if (cursorXPosition.current >= dimensions.container.width - scrollBoundary) {
      moveTextInPx = -(cursorXPosition.current - dimensions.container.width + scrollBoundary);
    }
    moveTextInPx = moveTextInPx * speedMultiplier;
    setTextPosition(current => Math.max(Math.min(current + moveTextInPx, 0), -dimensions.lengthDifference));
  }

  /**
   * Add moveText interval on hover
   */
  useEffect(() => {
    if (isHovered) {
      updateDimensions();

      const interval = setInterval(moveText, 100);

      return () => clearInterval(interval);
    }
  }, [isHovered]);

  return (
    <Styled.Container
      {...bind()}
      ref={containerRef}
      onPointerDown={undefined}
      onMouseMove={e => {
        if (dimensions) {
          cursorXPosition.current = e.pageX - dimensions.container.offsetLeft;
        }
      }}
      onMouseLeave={() => (cursorXPosition.current = undefined)}
      style={{
        height: dimensions ? `${dimensions.container.height}px` : undefined,
        maxWidth: dimensions ? `${dimensions.text.width}px` : undefined,
      }}
      {...props}>
      {!hideFade && (
        <>
          <Styled.Fade $position='left' $fadeColor={fadeColor} $isVisible={textPosition !== 0} />
          <Styled.Fade
            $position='right'
            $fadeColor={fadeColor}
            $isVisible={
              !!(
                dimensions &&
                dimensions.text.width > dimensions.container.width &&
                textPosition > dimensions.container.width - dimensions.text.width
              )
            }
          />
        </>
      )}
      <Styled.Text
        ref={textRef}
        style={{ transform: `translateX(${textPosition}px)`, transition: isDragged ? 'none' : undefined }}>
        {children}
      </Styled.Text>
    </Styled.Container>
  );
};

export default TextFadeOverflowScroll;
